import { useEffect, useState } from "react";
import { api, ApiError } from "./api.ts";
import { setHashCommunity, useCommunity } from "./community.ts";
import type { AdminCommunitySummary } from "./types.ts";

interface Props {
  // Surfaced upstream so we can render an inline error in the App chrome
  // if the communities endpoint blows up.
  onError?: (code: string | null) => void;
}

export function CommunitySelector({ onError }: Props) {
  const current = useCommunity();
  const [communities, setCommunities] = useState<AdminCommunitySummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api<{ items: AdminCommunitySummary[] }>("/admin/communities")
      .then((data) => {
        if (cancelled) return;
        setCommunities(data.items);
        onError?.(null);
      })
      .catch((e) => {
        if (cancelled) return;
        const code = e instanceof ApiError ? e.message : String(e);
        onError?.(code);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // onError is intentionally not in deps — callers pass a stable ref or
    // accept that the effect re-runs only on first mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    setHashCommunity(v === "" ? null : v);
  }

  const label = (() => {
    if (loading) return "Loading communities…";
    if (current === null) return `All communities (${communities.length})`;
    const c = communities.find((x) => x.id === current);
    return c ? `${c.name}` : `Community #${current}`;
  })();

  return (
    <div
      className="community-selector"
      data-current-community={current ?? "all"}
    >
      <label htmlFor="community-select" className="selector-label">
        Scope
      </label>
      <select
        id="community-select"
        className="luna-input luna-input-sm"
        value={current ?? ""}
        onChange={onChange}
        disabled={loading}
      >
        <option value="">All communities</option>
        {communities.map((c) => {
          const flags: string[] = [];
          if (!c.active) flags.push("inactive");
          if (c.pendingRestaurantCount > 0) {
            flags.push(`${c.pendingRestaurantCount} pending`);
          }
          if (c.openReportCount > 0) {
            flags.push(`${c.openReportCount} open reports`);
          }
          const suffix = flags.length > 0 ? ` — ${flags.join(", ")}` : "";
          return (
            <option key={c.id} value={c.id}>
              #{c.id} {c.name}{suffix}
            </option>
          );
        })}
      </select>
      <span className="meta community-selector-summary">{label}</span>
    </div>
  );
}
